import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import { Link } from '@/components/atoms/Link';
import LegacyTextButton from '@/components/atoms/legacy/TextButton';
import Logo from '@/components/molecules/Logo';
import { GOOGLE_TRACK_INFO } from '@/lib/constants';
import { AnalyticsEvent } from '@/lib/handleActionTracking';
import { useAnalytics } from '@/components/molecules/AnalyticsProvider';
import { ChevronUpIcon } from '@heroicons/react/24/outline';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/solid';
import React from 'react';
import { footerLinks } from '../FunnelFooter/FunnelFooter';
import SocialIconGroup from '../SocialIconGroup';

interface GeneralFooterLinksProps {
  subLinks: {
    newTab?: boolean;
    text: string;
    href: string;
    analyticsEvent?: AnalyticsEvent;
    hidden?: boolean;
  }[];
  sectionHeader: string;
}

const searchFooterLinks = [
  {
    text: 'New',
    href: '/new-cars',
    analyticsEvent: {
      ...GOOGLE_TRACK_INFO.inventoryHomePageButton,
      pageTarget: '/search',
    },
  },
  {
    text: 'Used',
    href: '/used-cars',
    analyticsEvent: {
      ...GOOGLE_TRACK_INFO.inventoryHomePageButton,
      pageTarget: '/search',
    },
  },
  ...(process.env.NEXT_PUBLIC_ENABLE_CUSTOM_ORDER === 'true'
    ? [
        {
          text: 'Custom order',
          href: '/build',
          analyticsEvent: {
            ...GOOGLE_TRACK_INFO.customOrderHomePageButton,
            pageTarget: '/build',
          },
        },
      ]
    : []),
  ...(process.env.NEXT_PUBLIC_ENABLE_SELL_MY_CAR === 'true'
    ? [
        {
          text: 'Sell my car',
          href: '/sell',
        },
      ]
    : []),
];

const discoverFooterLinks = [
  ...(process.env.NEXT_PUBLIC_ENABLE_EV_COMPARE === 'true'
    ? [
        {
          text: 'Compare EVs',
          href: '/compare',
          analyticsEvent: {
            ...GOOGLE_TRACK_INFO.compareEVHomePageButton,
            pageTarget: '/compare',
          },
        },
      ]
    : []),
  {
    text: 'News & Reviews',
    href: '/news',
    analyticsEvent: {
      ...GOOGLE_TRACK_INFO.newsHomePageButton,
      pageTarget: '/news',
    },
  },
  {
    text: 'FAQs',
    href: '/faq',
    analyticsEvent: {
      ...GOOGLE_TRACK_INFO.frequentlyAskedQuestionsButton,
      pageTarget: '/faq',
    },
  },
];

const partnerFooterLinks = [
  {
    text: 'Contact sales',
    href: 'mailto:hi@ev.com',
  },
  {
    text: 'Partner with ev.com',
    href: 'https://partner.ev.com/',
    analyticsEvent: {
      ...GOOGLE_TRACK_INFO.partnerHomePageButton,
      pageTarget: 'https://partner.ev.com/',
    },
    newTab: true,
  },
];

const GeneralFooterLinkComponent: React.FC<GeneralFooterLinksProps> = ({
  subLinks,
  sectionHeader,
}) => {
  const { handleActionTracking } = useAnalytics();
  return (
    <section className="mr-[40px] m:w-full l:w-[160px]">
      <h3 className="mb-l whitespace-nowrap text-h3SemiBold text-white l:text-h4SemiBold">
        {sectionHeader}
      </h3>
      <nav className="flex flex-col gap-s">
        {subLinks
          .filter((link) => !link.hidden)
          .map((link) => (
            <Link
              className="text-body2Light text-white hover:text-body2Regular hover:text-brandElectric"
              key={link.text}
              href={link.href}
              target={link.newTab ? '_blank' : undefined}
              rel={link.newTab ? 'noopener noreferrer' : undefined}
              onClick={(e) => {
                if (link.analyticsEvent) {
                  handleActionTracking(link.analyticsEvent);
                }
                if (!link.newTab) {
                  e?.preventDefault();
                  window.location.href = link.href;
                }
              }}
            >
              {link.text}
            </Link>
          ))}
      </nav>
    </section>
  );
};

const searchFooterLinksSection = (
  <GeneralFooterLinkComponent
    subLinks={searchFooterLinks}
    sectionHeader="Search"
  />
);

const discoverFooterLinksSection = (
  <GeneralFooterLinkComponent
    subLinks={discoverFooterLinks}
    sectionHeader="Discover"
  />
);

const partnerFooterLinksSection = (
  <GeneralFooterLinkComponent
    subLinks={partnerFooterLinks}
    sectionHeader="Partner with us"
  />
);

export const Footer: React.FC<{ className?: string; id?: string }> = ({
  className = '',
  id,
  ...props
}) => {
  const currentYear = new Date().getFullYear();
  const { handleActionTracking } = useAnalytics();
  return (
    <footer
      className={`relative py-[60px] text-white ${className}`}
      id={id}
      {...props}
    >
      <section className="mb-[40px] m:mb-[110px] m:flex m:flex-wrap l:flex-nowrap l:justify-between">
        <section className="flex m:basis-full m:justify-between l:basis-1/3 l:flex-col l:justify-start">
          <Link className="mb-[40px]" href="/" aria-label="home">
            <Logo className="relative w-[80px] m:w-[115px]" reverse />
          </Link>
          <section className="hidden gap-m m:flex">
            <SocialIconGroup
              keyPrefix="first-footer"
              iconClassName="hover:text-brandElectric"
            />
          </section>
        </section>
        <section className="l:basis-none mb-[40px] flex flex-grow flex-col gap-xl m:mb-[60px] m:basis-full m:flex-row m:gap-0 l:mb-0">
          {searchFooterLinksSection}
          {discoverFooterLinksSection}
          {partnerFooterLinksSection}
        </section>
        <section className="flex w-full flex-col justify-between gap-l border-t-[1px] border-t-neutral-600 pt-xl m:flex-row l:w-auto l:flex-col l:border-l-[1px] l:border-t-0 l:border-l-neutral-600 l:pl-[40px] l:pt-0">
          <section className="flex flex-col gap-l">
            <h3 className="text-h3SemiBold text-white m:text-h4SemiBold">
              Contact us
            </h3>
            <div className="text-body1Light">Email hi@ev.com</div>
          </section>
          <section>
            <Button
              aria-label="Send us feedback"
              variant={ButtonVariants.Secondary}
              theme="dark"
              size="small"
              icon={{
                icon: <ChatBubbleBottomCenterTextIcon className="h-xl w-xl" />,
                position: 'left',
              }}
              onClick={() =>
                window.open('https://forms.office.com/r/aePXTz7jfC', '_blank')
              }
            >
              Send us feedback
            </Button>
          </section>
        </section>
        <section className="flex gap-xl pt-[40px] m:hidden">
          <SocialIconGroup
            keyPrefix="second-footer"
            iconClassName="hover:text-brandElectric"
          />
        </section>
      </section>
      <section className="flex flex-col-reverse gap-s m:justify-between l:flex-row">
        <p className="mb-[6px] whitespace-nowrap text-body2Light text-neutral-200 l:mb-0">
          © {currentYear} EV.com Inc. All rights reserved
        </p>
        <nav className="flex flex-col m:flex-row m:gap-l">
          {footerLinks.map(({ text, href, analyticsEvent }) => (
            <Link
              className="!px-0 text-body2Light text-neutral-200 hover:text-brandElectric max-lg:mb-[4px]"
              key={text}
              href={href}
              onClick={(e) => {
                if (analyticsEvent) {
                  handleActionTracking(analyticsEvent);
                }
                e?.preventDefault();
                window.location.href = href;
              }}
            >
              {text}
            </Link>
          ))}
        </nav>
      </section>
      {/* Keep using legacy here for a temp solution, would update this in a separate PR */}
      <LegacyTextButton
        className="absolute bottom-full right-0 flex translate-y-2/4 items-center justify-center border border-neutral-400 bg-white p-[10px]"
        onClick={() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }}
        ariaLabel="Back to top"
      >
        <ChevronUpIcon stroke="#516670" width={24} height={24} />
      </LegacyTextButton>
    </footer>
  );
};

export default Footer;
